import { call, put, putResolve, select, takeEvery } from 'typed-redux-saga';

import { bffAPI, iframeAPI } from '@/api';
import { saveToken } from '@/api/utils/token.holder';
import { goalReached, YMGoals } from '@/infrastructure/ym';
import { makeSelectInitialization } from '@/model/auth/selectors';
import { InitialData } from '@/model/auth/types';
import { notifyGlobalInitializationStarted, notifyLogout } from '@/model/global/actions';
import { makeSelectIsFrameView } from '@/model/global/selectors';
import { withGlobalLock } from '@/model/global/utils';
import createCache from '@/utils/cache';
import { removeQueryParam } from '@/utils/http';
import { ActionWithPayload, takeEveryTypesafe } from '@/utils/redux';

import * as actions from './actions';

const cache = createCache<{ userId: string }>('MNX_WALLET_AUTH');
const selectIsFrame = makeSelectIsFrameView();

function* init(): Generator {
  let isNewUser = true;
  // clean auth cookie if in the iframe mode or accessed with userToken
  let cleanAuthCookie = false;
  let success = false;
  let initialData: InitialData | undefined;
  const isFrame = yield* select(selectIsFrame);
  try {
    // 1 priority: jwt token
    const userToken = new URLSearchParams(window.location.search).get('userToken');
    if (userToken && userToken.length) {
      cleanAuthCookie = true;
      goalReached(YMGoals.USER_LOGGED_IN_WITH_KEY);
      saveToken(userToken);
    } else {
      // 2 priority: otp
      const oneTimeToken = new URLSearchParams(window.location.search).get('token');
      if (oneTimeToken && oneTimeToken.length) {
        goalReached(YMGoals.USER_LOGGED_IN_WITH_OTP);
        const token = yield* call(bffAPI.auth, oneTimeToken);
        // FIXME: replace with cookie
        saveToken(token);
        if (isFrame) {
          cleanAuthCookie = true;
        }
      }
    }

    try {
      initialData = yield* call(bffAPI.queryAuthData);
      success = true;

      const cachedData = cache.read();
      if (cachedData?.userId && cachedData?.userId === initialData?.userId) {
        isNewUser = false;
      }
      if (cachedData?.userId && cachedData?.userId !== initialData?.userId) {
        goalReached(YMGoals.USER_ID_CHANGED);
      }
      cache.save({ userId: initialData.userId });
    } catch (e) {
      if (e.response?.status === 403) {
        cache.clear();
        return;
      }
    }

    if (cleanAuthCookie) {
      try {
        yield* call(bffAPI.logout);
      } catch (e) {
        // do nothing
      }
    }

    // clean both tokens from url
    removeQueryParam('userToken');
    removeQueryParam('token');
  } finally {
    yield putResolve(actions.storeAuth({ initialData, isNewUser, success }).pure);
    yield put(actions.notifyAuthorizationFinished({ isNewUser, success }).pure);
    if (isFrame) {
      iframeAPI.sendInitialized();
      if (!success) {
        goalReached(YMGoals.USER_AUTH_ERROR);
        iframeAPI.sendError('unauthorized');
      }
    }
  }
}

function* saveForbidden({ payload: isForbidden }: ActionWithPayload<boolean>): Generator {
  const { forbidden: wasForbidden } = yield* select(makeSelectInitialization());
  const isFrame = yield* select(selectIsFrame);
  if (isFrame && !wasForbidden && isForbidden) {
    iframeAPI.sendError('forbidden');
  }
  yield* put(actions.storeForbidden(isForbidden).pure);
}

function* logout(): Generator {
  try {
    goalReached(YMGoals.USER_LOGGED_OUT);
    yield* call(bffAPI.logout);
  } finally {
    cache.clear();
    yield put(actions.storeLogout().pure);
  }
}

export default function* rootSaga(): Generator {
  yield* takeEveryTypesafe(notifyGlobalInitializationStarted, withGlobalLock(init, 'common.messages.operations.init'));
  yield* takeEveryTypesafe(actions.saveForbidden, saveForbidden);
  yield* takeEvery(notifyLogout.type, withGlobalLock(logout, 'common.messages.operations.logout'));
}
